import axios, { AxiosError } from 'axios';
import { apiRoute } from '../constants/api-constants';
import { StaysApiError } from './stays-client';

/**
 * THE BUILDING LINK (Independent Stays plan IS7; lobbero_be/src/stays/routes/
 * building-links.js). A host's listing linked to another account's front desk. Each
 * side reads only its own fields; a code is shown once and never again.
 */

export type LinkStatus = 'requested' | 'approved' | 'ended' | 'refused';

export interface DeskLinkCode {
  id: string;
  status: string;
  expires_at: string;
  created_at: string;
}

export interface DeskLink {
  id: string;
  status: LinkStatus;
  apartment_label: string;
  requester_name: string | null;
  host_kind: string;
  desk_unit_id: number | null;
  unit_label: string | null;
  requested_at: string;
  decided_at: string | null;
}

export interface DeskLinksAnswer {
  codes: DeskLinkCode[];
  links: DeskLink[];
}

export interface NewLinkCode {
  id: string;
  code: string;
  expires_at: string;
}

export interface HostLink {
  id?: string;
  status: LinkStatus | 'none';
  apartment_label?: string;
  building_name?: string;
  city?: string | null;
  requested_at?: string;
  decided_at?: string | null;
  ended_at?: string | null;
  ended_by?: 'host' | 'desk' | null;
  desk_available?: boolean;
}

const wrap = async <T>(p: Promise<{ data: T }>, fallback: string): Promise<T> => {
  try {
    const { data } = await p;

    return data;
  } catch (e) {
    const err = e as AxiosError<Record<string, unknown>>;
    if (err && err.response) throw new StaysApiError(err.response.status, err.response.data || null, fallback);
    throw new StaysApiError(0, null, fallback);
  }
};

const desk = `${apiRoute}/stays/building-links`;
const host = (listingId: string) => `${apiRoute}/stays/listings/${encodeURIComponent(listingId)}/building-link`;

export const getDeskLinks = (buildingId: number): Promise<DeskLinksAnswer> =>
  wrap(axios.get(desk, { params: { building_id: buildingId } }), 'Could not load the short-stay hosts.');

export const createLinkCode = (buildingId: number): Promise<NewLinkCode> =>
  wrap(axios.post(`${desk}/codes`, { building_id: buildingId }), 'Could not create a link code.');

export const revokeLinkCode = (codeId: string): Promise<void> =>
  wrap(axios.post(`${desk}/codes/${encodeURIComponent(codeId)}/revoke`), 'Could not cancel the code.').then(() => undefined);

export const decideLink = (
  linkId: string,
  input: { approve: true; desk_unit_id: number } | { approve: false }
): Promise<{ id: string; status: LinkStatus }> =>
  wrap(axios.post(`${desk}/${encodeURIComponent(linkId)}/decide`, input), 'Could not save your decision.');

export const endDeskLink = (linkId: string): Promise<{ id: string; status: LinkStatus }> =>
  wrap(axios.post(`${desk}/${encodeURIComponent(linkId)}/end`), 'Could not end the link.');

export const getHostLink = (listingId: string): Promise<HostLink> =>
  wrap(axios.get(host(listingId)), 'Could not load the front desk link.');

export const requestHostLink = (listingId: string, input: { code: string; apartment_label: string }): Promise<HostLink> =>
  wrap(axios.post(host(listingId), input), 'Could not send the request.');

export const endHostLink = (listingId: string): Promise<HostLink> =>
  wrap(axios.post(`${host(listingId)}/end`), 'Could not end the link.');
